import React, { useEffect, useState } from "react";

interface ShifterArrowProps {
  fromId: string;
  toId: string;
  color?: string;
  duration?: number;
}

const ShifterArrowConnector: React.FC<ShifterArrowProps> = ({
  fromId,
  toId,
  color = "#D046E8",
  duration = 0.5,
}) => {
  const [coordinates, setCoordinates] = useState({ x1: 0, y1: 0, x2: 0, y2: 0 });
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const update = () => {
        const a = document.getElementById(fromId)?.getBoundingClientRect();
        const b = document.getElementById(toId)?.getBoundingClientRect();

        if (!a || !b) return;

        setCoordinates({
        x1: a.left + a.width / 2 + window.scrollX,
        y1: a.top + a.height / 2 + window.scrollY,
        x2: b.left + b.width / 2 + window.scrollX,
        y2: b.top + b.height / 2 + window.scrollY,
        });
    };
    
    setTimeout(() => {
      update();
    }, 500);

    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    window.addEventListener("load", update);
    window.addEventListener("click", update);

    return () => {
        window.removeEventListener("resize", update);
        window.removeEventListener("scroll", update, true);
        window.removeEventListener("load", update);
        window.removeEventListener("click", update);
    };
  }, [fromId, toId]);

  useEffect(() => {
    const { x1, y1, x2, y2 } = coordinates;
    if (x1 === x2 && y1 === y2) return;
    
    setAnimate(false);

    const tid = setTimeout(() => setAnimate(true), 20);

    return () => clearTimeout(tid);
  }, [coordinates]);

  const { x1, y1, x2, y2 } = coordinates;
  const length = Math.hypot(x2 - x1, y2 - y1);
  const drawTime = Math.max(duration, 1);

  const strokeWidth = 3;
  const borderWidth = strokeWidth + 2;

  return (
    <svg
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 1250,
      }}
    >
      <defs>
        {/* Destination arrow icon */}
        <marker
          id="arrowheadColored"
          markerWidth="6"
          markerHeight="4"
          refX="4"
          refY="2"
          orient="auto"
        >
          <path
            d="M0,0 L6,2 L0,4 Z"
            fill={color}
            stroke="#fff"
            strokeWidth="0.5"
          />
        </marker>

        {/* Initial circle icon */}
        <marker
          id="circleColored"
          markerWidth="4"
          markerHeight="4"
          refX="2"
          refY="2"
          orient="auto"
        >
          <circle cx="2" cy="2" r="1.5" fill={color} stroke="#fff" strokeWidth="0.5" />
        </marker>
      </defs>

      {/* Line border */}
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#fff"
        strokeWidth={borderWidth}
        markerStart="url(#circleBorder)"
        markerEnd="url(#arrowheadBorder)"
        strokeDasharray={length}
        strokeDashoffset={length}
        style={{
          animation: animate
            ? `drawArrow ${drawTime}s ease-out forwards`
            : "none",
        }}
      />

      {/* Main line */}
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={color}
        strokeWidth={strokeWidth}
        markerStart="url(#circleColored)"
        markerEnd="url(#arrowheadColored)"
        strokeDasharray={length}
        strokeDashoffset={length}
        style={{
          animation: animate
            ? `drawArrow ${drawTime}s ease-out forwards`
            : "none",
        }}
      />

      <style>{`
        @keyframes drawArrow {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </svg>
  );
};

export default ShifterArrowConnector;